import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Play, Loader, CheckCircle2, RotateCcw, Send, Pencil,
  Mail, MessageSquare, RefreshCw,
} from "lucide-react";
import api from "../lib/api";
import { StatusBadge, PriorityBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [reviewOpen, setReviewOpen] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [assignable, setAssignable] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/tasks/${id}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.friendlyMessage);
      navigate("/tasks");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action) => {
    setBusy(true);
    try {
      await api.post(`/tasks/${id}/status`, { action });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
    setBusy(false);
  };

  const doReview = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${id}/review`, { action: reviewOpen, comment: reviewComment });
      toast.success(reviewOpen === "approve" ? "Task approved" : "Task reopened");
      setReviewOpen(null); setReviewComment("");
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
    setBusy(false);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    try {
      await api.post(`/tasks/${id}/comments`, { text: comment });
      setComment("");
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  const openEdit = async () => {
    const t = data.task;
    setEditForm({
      title: t.title, description: t.description, priority: t.priority,
      due_date: t.due_date || "", category: t.category, assigned_to: t.assigned_to,
      notify_whatsapp: false, notify_email: false,
    });
    try {
      const res = await api.get("/assignable-users");
      setAssignable(res.data);
    } catch (e) { /* ignore */ }
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/tasks/${id}`, editForm);
      toast.success("Task updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
    setBusy(false);
  };

  const retryNotification = async (logId) => {
    try {
      const res = await api.post(`/notification-logs/${logId}/retry`);
      toast.success(`Retry ${res.data.status}`);
      load();
    } catch (e) { toast.error(e.friendlyMessage); }
  };

  if (!data) return <div className="flex h-64 items-center justify-center text-slate-400">Loading task...</div>;

  const { task, comments, activity, notification_logs, permissions, assignee_contact } = data;

  return (
    <div className="animate-fade-in space-y-5" data-testid="task-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-primary" data-testid="task-back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <StatusBadge status={task.status} overdue={task.is_overdue} />
                  <PriorityBadge priority={task.priority} />
                  <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{task.category}</span>
                </div>
                <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900" data-testid="task-detail-title">{task.title}</h1>
              </div>
              {permissions.can_edit && (
                <Button variant="outline" size="sm" data-testid="task-edit-button" onClick={openEdit}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit / Reassign
                </Button>
              )}
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{task.description || "No description provided."}</p>
            {task.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {task.tags.map((t) => <span key={t} className="rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-medium text-teal-700">#{t}</span>)}
              </div>
            )}
            {task.review_comment && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <span className="font-semibold">Reviewer note:</span> {task.review_comment}
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-2 border-t border-slate-100 pt-5">
              {permissions.can_start && task.status === "new" && (
                <Button data-testid="task-start-button" onClick={() => doAction("start")} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700">
                  <Play className="mr-1.5 h-4 w-4" /> Start Task
                </Button>
              )}
              {permissions.can_start && task.status === "started" && (
                <Button data-testid="task-inprocess-button" onClick={() => doAction("in_process")} disabled={busy} className="bg-sky-600 hover:bg-sky-700">
                  <Loader className="mr-1.5 h-4 w-4" /> Move to In Process
                </Button>
              )}
              {permissions.can_mark_done && (
                <Button data-testid="task-mark-done-button" onClick={() => doAction("done")} disabled={busy} className="bg-teal-600 hover:bg-teal-700">
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Mark as Done
                </Button>
              )}
              {permissions.can_review && (
                <>
                  <Button data-testid="task-approve-button" onClick={() => setReviewOpen("approve")} className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve
                  </Button>
                  <Button data-testid="task-reopen-button" variant="outline" onClick={() => setReviewOpen("reopen")}
                    className="border-rose-300 text-rose-600 hover:bg-rose-50">
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Reopen
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="mb-4 font-heading text-lg font-bold text-slate-900">Comments</h3>
            <form onSubmit={addComment} className="mb-5 flex gap-2">
              <Input data-testid="task-comment-input" placeholder="Add an update or comment..." value={comment}
                onChange={(e) => setComment(e.target.value)} />
              <Button data-testid="task-comment-submit" type="submit" className="bg-primary hover:bg-[#1E3A8A]">
                <Send className="h-4 w-4" />
              </Button>
            </form>
            <div className="space-y-4">
              {comments.length === 0 && <p className="text-sm text-slate-400">No comments yet.</p>}
              {comments.map((c) => (
                <div key={c.id} data-testid={`task-comment-${c.id}`} className="rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">{c.user_name}</span>
                    <span className="text-xs text-slate-400">{fmt(c.created_at)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-700">{c.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="mb-5 font-heading text-lg font-bold text-slate-900">Activity Timeline</h3>
            <div className="relative space-y-5 pl-6" data-testid="task-activity-timeline">
              <div className="absolute bottom-2 left-[7px] top-2 w-px bg-slate-200" />
              {activity.map((a) => (
                <div key={a.id} className="relative">
                  <span className="absolute -left-6 top-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-teal-600 shadow" />
                  <div className="text-sm font-semibold text-slate-800">{a.detail || a.action}</div>
                  <div className="text-xs text-slate-500">
                    {a.actor_name} - {fmt(a.created_at)}
                    {a.old_value && a.new_value && a.old_value !== a.new_value && (
                      <span className="ml-1 font-data">({a.old_value} → {a.new_value})</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Details</h3>
            <dl className="space-y-3 text-sm">
              {[
                ["Assigned To", task.assigned_to_name],
                ["Created By", task.created_by_name],
                ["Department", task.department_name],
                ["Due Date", task.due_date ? `${task.due_date}${task.due_time ? " " + task.due_time : ""}` : "-"],
                ["Expected Completion", task.expected_completion_date || "-"],
                ["Created", fmt(task.created_at)],
                ["Started", fmt(task.started_at)],
                ["Marked Done", fmt(task.completed_at)],
                ["Reviewed", fmt(task.reviewed_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-right font-medium text-slate-800">{v}</dd>
                </div>
              ))}
              {task.recurring?.enabled && (
                <div className="flex justify-between gap-3">
                  <dt className="text-slate-500">Recurring</dt>
                  <dd className="rounded bg-teal-50 px-2 py-0.5 text-xs font-semibold capitalize text-teal-700">{task.recurring.frequency}</dd>
                </div>
              )}
            </dl>
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Assignee Contact</div>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  <span className="truncate">{assignee_contact.email || "No email on file"}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                  <span>{assignee_contact.mobile || "No WhatsApp number on file"}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="task-notification-history">
            <h3 className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Notification History</h3>
            {notification_logs.length === 0 && <p className="text-sm text-slate-400">No outbound notifications sent.</p>}
            <div className="space-y-3">
              {notification_logs.map((n) => (
                <div key={n.id} data-testid={`notif-log-${n.id}`} className="rounded-lg border border-slate-100 px-3 py-2.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 font-semibold capitalize text-slate-800">
                      {n.channel === "email" ? <Mail className="h-3.5 w-3.5" /> : <MessageSquare className="h-3.5 w-3.5" />}
                      {n.channel}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                      n.status === "sent" ? "bg-emerald-50 text-emerald-700"
                      : n.status === "simulated" ? "bg-sky-50 text-sky-700"
                      : "bg-rose-50 text-rose-700"}`}>
                      {n.status}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">{n.recipient_name} - {fmt(n.created_at)}</div>
                  {n.failure_reason && <div className="mt-1 text-xs text-rose-600">{n.failure_reason}</div>}
                  {n.detail && <div className="mt-1 text-xs text-slate-400">{n.detail}</div>}
                  {n.status === "failed" && (
                    <button onClick={() => retryNotification(n.id)} data-testid={`notif-retry-${n.id}`}
                      className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      <RefreshCw className="h-3 w-3" /> Retry
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!reviewOpen} onOpenChange={() => setReviewOpen(null)}>
        <DialogContent data-testid="task-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{reviewOpen === "approve" ? "Approve Task" : "Reopen Task"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            {reviewOpen === "approve"
              ? "Approving marks this task as finally completed."
              : "Reopening returns the task to In Process. Please explain what needs more work."}
          </p>
          <Textarea data-testid="task-review-comment" placeholder={reviewOpen === "reopen" ? "Reason for reopening (required)..." : "Optional review comment..."}
            value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(null)}>Cancel</Button>
            <Button data-testid="task-review-confirm" disabled={busy || (reviewOpen === "reopen" && !reviewComment.trim())}
              onClick={doReview}
              className={reviewOpen === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}>
              {reviewOpen === "approve" ? "Approve" : "Reopen Task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="task-edit-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Edit Task</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input data-testid="task-edit-title" value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea data-testid="task-edit-description" rows={3} value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                  <SelectTrigger data-testid="task-edit-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["low", "medium", "high", "critical"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Due Date</Label>
                <Input data-testid="task-edit-due-date" type="date" value={editForm.due_date || ""} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Reassign To</Label>
              <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm({ ...editForm, assigned_to: v })}>
                <SelectTrigger data-testid="task-edit-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {assignable.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} - {u.designation || u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {editForm.assigned_to !== task.assigned_to && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Notify new assignee</div>
                <div className="flex gap-5">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid="task-edit-notify-whatsapp" checked={!!editForm.notify_whatsapp}
                      onCheckedChange={(v) => setEditForm({ ...editForm, notify_whatsapp: !!v })} />
                    WhatsApp
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid="task-edit-notify-email" checked={!!editForm.notify_email}
                      onCheckedChange={(v) => setEditForm({ ...editForm, notify_email: !!v })} />
                    Email
                  </label>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="task-edit-save" onClick={saveEdit} disabled={busy} className="bg-primary hover:bg-[#1E3A8A]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
